import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { AuthContext } from "../../context/AuthContext";
import axiosClient from "../../api/axiosClient";

import AddTenant from "./AddTenant";
import TenantEdit from "./TenantEdit";
import ViewTenant from "./ViewTenant";
import TenantRolePermissionMatrix from "../permissions/TenantRolePermissionMatrix";

import {
  MdVisibility,
  MdEdit,
  MdMoreVert,
  MdBlock,
  MdArrowDropUp,
  MdArrowDropDown,
  MdCheckCircle,
} from "react-icons/md";
import "../Users.css";
import "../../styles/TenantDrawer.css";
/***********************************
 * ✅ Helpers
 ***********************************/
const safeString = (v) => (v === null || v === undefined ? "" : String(v));

const sortList = (list, key, dir) =>
  [...list].sort((a, b) =>
    dir === "asc"
      ? safeString(a?.[key]).localeCompare(safeString(b?.[key]))
      : safeString(b?.[key]).localeCompare(safeString(a?.[key]))
  );

/***********************************
 * ✅ Action Menu (Portal)
 ***********************************/
function ActionMenu({ disabled, items }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuW = 200;
    const gap = 8;
    const left = Math.max(8, r.left - menuW - gap);
    setPos({ top: r.top - 4, left });

    const close = (e) => {
      if (!btnRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close, true);
    window.addEventListener("scroll", close);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("mousedown", close, true);
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-sm btn-action-more"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        title="Actions"
      >
        <MdMoreVert size={18} />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            className="dropdown-menu show shadow-sm actionmenu-portal"
            style={{
              position: "fixed",
              zIndex: 5000,
              minWidth: 200,
              ...pos,
            }}
          >
            {(items || []).map((it, i) =>
              it?.type === "divider" ? (
                <div key={i} className="dropdown-divider" />
              ) : (
                <button
                  key={i}
                  className={`dropdown-item ${it?.className || ""}`}
                  onClick={() => {
                    setOpen(false);
                    it?.onClick?.();
                  }}
                >
                  {it?.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
}

/***********************************
 * ✅ TenantList Component
 ***********************************/
export default function TenantList() {
  const { hasPermission } = useContext(AuthContext);

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [editTenant, setEditTenant] = useState(null);
  const [viewTenant, setViewTenant] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [activeTenantId, setActiveTenantId] = useState(null);

  const pageSize = 20;
  const [page, setPage] = useState(1);

  /***********************************
   * ✅ Load Tenants
   ***********************************/
  const loadTenants = async () => {
    try {
      setLoading(true);
      const res = await axiosClient.get("/tenant/api/list");
      setTenants(res.data.data || []);
    } catch {
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  /***********************************
   * ✅ Sorting & Filtering
   ***********************************/
  const filtered = useMemo(() => {
    let list = [...tenants];
    const q = search.toLowerCase();

    if (q)
      list = list.filter(
        (t) =>
          safeString(t.name).toLowerCase().includes(q) ||
          safeString(t.code).toLowerCase().includes(q)
      );

    if (fStatus) list = list.filter((t) => t.status === fStatus);

    return sortList(list, sortKey, sortDir);
  }, [tenants, search, fStatus, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pageList = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : ""}`}>
      {sortKey === col
        ? sortDir === "asc"
          ? <MdArrowDropUp />
          : <MdArrowDropDown />
        : <MdArrowDropDown />}
    </span>
  );

  /***********************************
   * ✅ Tenant Status Handlers
   ***********************************/
  const suspendTenant = async (tenant) => {
    if (!window.confirm(`Suspend ${tenant.name}?`)) return;
    await axiosClient.put(`/tenant/api/${tenant.id}`, { status: "Suspended" });
    loadTenants();
  };

  const activateTenant = async (tenant) => {
    await axiosClient.put(`/tenant/api/${tenant.id}`, { status: "Active" });
    loadTenants();
  };

  if (loading) return <div className="text-center p-4">Loading…</div>;

  /***********************************
   * ✅ Render
   ***********************************/
  return (
    <div className="container-fluid">
      {/* ✅ Header */}
      <div className="users-hero shadow-sm">
        <div>
          <div className="h4 fw-bold mb-1 text-white">Tenant Management</div>
          <div className="text-white-50 small">
            Create, manage and control tenants.
          </div>
        </div>
        {hasPermission("TENANT_MANAGE", "create") && (
          <button
            className="btn-gradient-primary"
            onClick={() => setShowAdd(true)}
          >
            + Add Tenant
          </button>
        )}
      </div>

      {/* ✅ Filters */}
      <div className="users-filter-toolbar">
        <input
          className="form-control"
          placeholder="Search tenant name/code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <select
          className="form-select"
          value={fStatus}
          onChange={(e) => setFStatus(e.target.value)}
        >
          <option value="">Status</option>
          <option value="Active">Active</option>
          <option value="Suspended">Suspended</option>
        </select>
      </div>

      {/* ✅ Table */}
      <div className="card shadow-sm">
        <table className="table table-sm users-table mb-0">
          <thead>
            <tr>
              <th onClick={() => toggleSort("name")}>
                Name <SortIcon col="name" />
              </th>
              <th onClick={() => toggleSort("code")}>
                Code <SortIcon col="code" />
              </th>
              <th>Domain</th>
              <th>Status</th>
              <th onClick={() => toggleSort("created_at")}>
                Created <SortIcon col="created_at" />
              </th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>

          <tbody>
            {pageList.map((t) => (
              <tr key={t.id}>
                <td>{safeString(t.name)}</td>
                <td>{safeString(t.code)}</td>
                <td>{safeString(t.domain || "—")}</td>
                <td>
                  <span
                    className={`badge ${
                      t.status === "Active" ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {t.status}
                  </span>
                </td>
                <td>{safeString(t.created_at).split("T")[0]}</td>

                <td className="text-end d-flex align-items-center justify-content-end gap-2">
                  {/* View */}
                  <button
                    className="btn btn-sm btn-action-view"
                    title="View"
                    onClick={() => setViewTenant(t)}
                  >
                    <MdVisibility size={15} />
                  </button>

                  {/* Edit */}
                  <button
                    className="btn btn-sm btn-action-edit"
                    title="Edit"
                    onClick={() => setEditTenant(t)}
                  >
                    <MdEdit size={15} />
                  </button>

                  {/* More */}
                  <ActionMenu
                    items={[
                      t.status === "Active"
                        ? {
                            label: (
                              <>
                                <MdBlock className="text-warning" size={16} /> Suspend
                              </>
                            ),
                            onClick: () => suspendTenant(t),
                          }
                        : {
                            label: (
                              <>
                                <MdCheckCircle
                                  className="text-success"
                                  size={16}
                                />{" "}
                                Activate
                              </>
                            ),
                            onClick: () => activateTenant(t),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ✅ Pagination */}
        <div className="users-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={page === i + 1 ? "active" : ""}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* ✅ Drawers */}
      {showAdd && (
        <>
          <div className="drawer-overlay" onClick={() => setShowAdd(false)} />
          <div className="drawer-panel">
            <AddTenant
              onClose={() => setShowAdd(false)}
              onSuccess={loadTenants}
            />
          </div>
        </>
      )}

      {viewTenant && (
        <>
          <div className="drawer-overlay" onClick={() => setViewTenant(null)} />
          <div className="drawer-panel">
            <ViewTenant
              tenantData={viewTenant}
              onClose={() => setViewTenant(null)}
              onEdit={(t) => {
                setViewTenant(null);
                setEditTenant(t);
              }}
            />
          </div>
        </>
      )}

      {editTenant && (
  <>
    <div className="drawer-overlay" onClick={() => setEditTenant(null)} />
    <div className="drawer-panel">
      <TenantEdit
        tenantData={editTenant}
        onClose={() => setEditTenant(null)}
        onSuccess={() => {
          setEditTenant(null);
          loadTenants();
        }}
        onOpenPermissions={(tenantId) => {
          // ✅ Close edit drawer first
          setEditTenant(null);
          // ✅ Wait a short delay to prevent flicker
          setTimeout(() => setActiveTenantId(tenantId), 200);
        }}
      />
    </div>
  </>
)}

      {/* ✅ Tenant Role Permission Drawer */}
      {activeTenantId &&
  ReactDOM.createPortal(
    <>
      <div
        className="tenant-drawer-overlay"
        onClick={() => setActiveTenantId(null)}
      />
      <div className="tenant-drawer-panel">
        <TenantRolePermissionMatrix
          tenantId={activeTenantId}
          onClose={() => setActiveTenantId(null)}
        />
      </div>
    </>,
    document.body
  )}
    </div>
  );
}
